import React, { useEffect, useState } from 'react';
import { Button, FormControlLabel, Radio, RadioGroup, Stack, Typography } from '@mui/material';
import { FactCheck } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { useOrgFeatureFlag } from '../hooks/useOrgFeatureFlags';
import { useToast } from '../contexts/ToastContext';
import { InlineMessage, LoadingState, SectionCard } from './ui';
import {
  PRICE_CHANGE_REVIEW_SETTING_KEY,
  PriceChangeReviewSetting,
  fetchPriceChangeReviewSetting,
  savePriceChangeReviewSetting,
} from '../services/priceChangeReviewApi';

/**
 * Jordan decides whether incoming price and rebate changes need an owner or
 * admin's approval before they reach the connected stores (flag
 * `price_change_review`; Estimate row B38, #490). Hidden with the flag off.
 */
export const PriceChangeReviewCard: React.FC = () => {
  const { t } = useAppTranslation();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { enabled } = useOrgFeatureFlag('price_change_review');
  const [selected, setSelected] = useState<PriceChangeReviewSetting | ''>('');

  const { data, isLoading, isError } = useQuery({
    queryKey: PRICE_CHANGE_REVIEW_SETTING_KEY,
    queryFn: () => fetchPriceChangeReviewSetting(t('store.priceChangeReview.loadError')),
    enabled,
  });

  useEffect(() => {
    if (data) setSelected(data);
  }, [data]);

  const save = useMutation({
    mutationFn: (setting: PriceChangeReviewSetting) =>
      savePriceChangeReviewSetting(setting, t('store.priceChangeReview.saveError')),
    onSuccess: () => {
      showToast(t('store.priceChangeReview.saved'), 'success');
      queryClient.invalidateQueries({ queryKey: PRICE_CHANGE_REVIEW_SETTING_KEY });
    },
    onError: (error: Error) => showToast(error.message, 'error'),
  });

  if (!enabled) return null;

  return (
    <SectionCard title={t('store.priceChangeReview.title')} leading={<FactCheck color="primary" />}>
      {isLoading ? (
        <LoadingState />
      ) : isError || !data ? (
        <InlineMessage severity="error">{t('store.priceChangeReview.loadError')}</InlineMessage>
      ) : (
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {t('store.priceChangeReview.intro')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('store.priceChangeReview.scope')}
          </Typography>
          <RadioGroup
            value={selected}
            onChange={(event) => setSelected(event.target.value as PriceChangeReviewSetting)}
            aria-label={t('store.priceChangeReview.title')}
          >
            {(['off', 'require_approval'] as const).map((value) => (
              <FormControlLabel
                key={value}
                value={value}
                control={<Radio size="small" />}
                label={
                  <Stack>
                    <Typography variant="body2">{t(`store.priceChangeReview.${value}.label`)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t(`store.priceChangeReview.${value}.description`)}
                    </Typography>
                  </Stack>
                }
                sx={{ alignItems: 'flex-start', mb: 1 }}
              />
            ))}
          </RadioGroup>
          <Typography variant="caption" color="text.secondary">
            {t('store.priceChangeReview.ownerOnly')}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              size="small"
              disabled={!selected || selected === data || save.isPending}
              onClick={() => selected && save.mutate(selected)}
            >
              {t('common.save')}
            </Button>
          </Stack>
        </Stack>
      )}
    </SectionCard>
  );
};

export default PriceChangeReviewCard;
